let erudaBootstrapped = false
let relayPatched = false

/**
 * Single app-wide debug switch: append `?debug` to the URL.
 * Turns on Eruda, dev-server log relay, AR debug wall/cube, and any future
 * debug-only behavior. No other query keys or localStorage toggles.
 */
export function isUrlDebugEnabled() {
  if (typeof window === 'undefined') return false
  try {
    return new URLSearchParams(window.location.search).has('debug')
  } catch {
    return false
  }
}

function shouldEnableDebugConsole() {
  return isUrlDebugEnabled()
}

// Serialise any value to a readable string (handles Error, objects, etc.)
function serialize(val) {
  if (val instanceof Error) return `${val.name}: ${val.message}`
  if (typeof val === 'object' && val !== null) {
    try { return JSON.stringify(val) } catch { return String(val) }
  }
  return String(val)
}

// Monkey-patch console.* to also POST each call to the webpack-dev-server
// /log-relay endpoint, which prints it colour-coded in the terminal.
function installRelayPatch() {
  if (relayPatched) return
  relayPatched = true

  const RELAY = '/log-relay'
  const LEVELS = ['log', 'info', 'warn', 'error', 'debug']

  LEVELS.forEach((level) => {
    const original = console[level].bind(console)
    console[level] = (...args) => {
      original(...args)
      try {
        const payload = JSON.stringify({
          level,
          args: args.map(serialize),
          url: window.location.href,
        })
        // sendBeacon is fire-and-forget and survives page unloads
        const blob = new Blob([payload], {type: 'application/json'})
        if (!navigator.sendBeacon(RELAY, blob)) {
          // Fall back to a non-blocking fetch if sendBeacon returns false
          fetch(RELAY, {
            method: 'POST',
            body: payload,
            headers: {'Content-Type': 'application/json'},
            keepalive: true,
          }).catch(() => {})
        }
      } catch { /* never let relay errors break the app */ }
    }
  })
}

export async function initDebugConsole() {
  if (!shouldEnableDebugConsole()) return

  // Terminal relay + Eruda are both gated on ?debug (see isUrlDebugEnabled).
  installRelayPatch()

  if (erudaBootstrapped) return
  erudaBootstrapped = true

  try {
    const {default: eruda} = await import('eruda')
    eruda.init()
    console.info('[debug] Eruda + terminal relay enabled (?debug)')
  } catch (error) {
    console.warn('[debug] Failed to load Eruda:', error)
  }
}

export default initDebugConsole
